// src/components/EventForm/ImageUploader.js
import React, { useState } from 'react';
import styled from 'styled-components';
import UploadFileIcon from '../../components/Icons/UploadFileIcon';
import Typography from '../../components/Typography';
import { getEventTypeSvgIcon } from '../../utilities';
import { theme } from '../../theme';
import { useTranslation } from 'react-i18next';
import InfoCircleOutlineIcon from '../Icons/InfoCircleOutlineIcon';
import WarningOutlineIcon from '../Icons/WarningOutlineIcon';
import Tooltip from '../Tooltip/Tooltip';
import ImageSelectionModal from '../Modal/ImageSelectionModal';

const ImageUploader = ({ file, imageUrl, onFileChange, eventType, error, warning }) => {
  const { t } = useTranslation('createEvent');
  const [showModal, setShowModal] = useState(false);

  const handleImageSelect = (selection) => {
    if (selection.type === 'file') {
      // Simular evento de cambio de archivo para mantener compatibilidad
      const mockEvent = {
        target: {
          files: [selection.data]
        }
      };
      onFileChange(mockEvent);
    } else if (selection.type === 'preset') {
      // Para imágenes prediseñadas, crear un evento mock con la URL
      const mockEvent = {
        target: {
          files: null,
          presetImageUrl: selection.data
        }
      };
      onFileChange(mockEvent);
    }
  };

  const handleClick = () => {
    setShowModal(true);
  };

  const renderContent = () => {
    if (file) {
      return (
        <>
          <Preview>
            <Image src={URL.createObjectURL(file)} alt="Event" />
            <InputOverlay onClick={handleClick}>
              <InputLabel className={error ? 'error' : ''}>
                <LabelContentWrapper>
                  <LabelButtonHighlighted>
                    <UploadFileIcon fill={theme.colors.defaultStrong} />
                    <Typography as="p" $variant="body-1-semibold" color={theme.colors.defaultStrong}>
                      {t('eventForm.eventImage.imageUpload')}
                    </Typography>
                  </LabelButtonHighlighted>
                  <Tooltip
                    text={t('eventForm.eventImage.recommendations')}
                    maxWidth='200px'
                    position="left"
                  >
                    <TooltipButton>
                      <InfoCircleOutlineIcon width="20px" height="20px" fill={theme.colors.defaultSubtle} />
                    </TooltipButton>
                  </Tooltip>
                </LabelContentWrapper>
              </InputLabel>
            </InputOverlay>
          </Preview>
          {error && (
            <ErrorMessage>
              <Typography as="p" $variant="body-2-medium" color={theme.colors.errorMain}>
                {error}
              </Typography>
            </ErrorMessage>
          )}
          {warning && (
            <StyledWarning as="p" $variant="body-2-medium" color={theme.colors.warningMain}>
              <WarningOutlineIcon width="20px" height="20px" fill={theme.colors.warningMain} />
              {warning}
            </StyledWarning>
          )}

        </>
      );
    } else if (imageUrl) {
      return (
        <>
          <Preview>
            <Image src={imageUrl} alt="Event" />
            <InputOverlay onClick={handleClick}>
              <InputLabel className={error ? 'error' : ''}>
                <LabelContentWrapper>
                  <LabelButton>
                    <UploadFileIcon fill={theme.colors.defaultStrong} />
                    <Typography as="p" $variant="body-1-semibold" color={theme.colors.defaultStrong}>
                      {t('eventForm.eventImage.imageUpload')}
                    </Typography>
                  </LabelButton>
                </LabelContentWrapper>
              </InputLabel>
            </InputOverlay>
          </Preview>
          {error && (
            <ErrorMessage>
              <Typography as="p" $variant="body-2-medium" color={theme.colors.errorMain}>
                {error}
              </Typography>
            </ErrorMessage>
          )}
        </>
      );
    } else {
      return (
        <>
          <EmptyState>
            {getEventTypeSvgIcon(eventType, theme.colors.defaultSubtle)}

            <InputOverlay onClick={handleClick}>
              <InputLabel className={error ? 'error' : ''}>
                <LabelContentWrapperEmpty>
                  <LabelButton>
                    <UploadFileIcon fill={theme.colors.defaultStrong} />
                    <Typography as="p" $variant="body-1-semibold" color={theme.colors.defaultStrong}>
                      {t('eventForm.eventImage.imageUpload')}
                    </Typography>
                  </LabelButton>
                  <Tooltip
                    text={t('eventForm.eventImage.recommendations')}
                    maxWidth='200px'
                    position="left"
                  >
                    <TooltipButton>
                      <InfoCircleOutlineIcon width="20px" height="20px" fill={theme.colors.defaultSubtle} />
                    </TooltipButton>
                  </Tooltip>
                </LabelContentWrapperEmpty>
              </InputLabel>
            </InputOverlay>
          </EmptyState>
          {error && (
            <ErrorMessage>
              <Typography as="p" $variant="body-2-medium" color={theme.colors.errorMain}>
                {error}
              </Typography>
            </ErrorMessage>
          )}
        </>
      );
    }
  };

  return (
    <Container>
      {renderContent()}
      {showModal && (
        <ImageSelectionModal
          onClose={() => setShowModal(false)}
          onImageSelect={handleImageSelect}
          eventType={eventType}
        />
      )}
    </Container>
  );
};

export default ImageUploader;

const Container = styled.div`
  width: 100%;
`;

const Preview = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: ${({ theme }) => theme.radius.sm};
  position: relative;
  transition: all 0.3s;
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
  
  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;

const EmptyState = styled(Preview)`
  width: 100%;
  // height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const InputOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
`;

const InputLabel = styled.div`
  cursor: pointer;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.radius.sm};
  transition: all 0.3s;

  &.error {
    border: 1px solid ${({ theme }) => theme.colors.errorMain};
  }
`;

const LabelButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: ${({ theme }) => theme.radius.xs};
  padding: ${({ theme }) => theme.sizing.xs};
  height: 40px;
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 32px;
  }
`;

const LabelButtonHighlighted = styled(LabelButton)`
  box-shadow: rgba(16, 17, 15, 0.20) 0px 0px 24px;
`;

const TooltipButton = styled(LabelButton)`
  width: 40px;
  padding: 0;
  align-items: center;
  justify-content: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 32px;
  }
`;

const LabelContentWrapper = styled.div`
  display: flex;
  // flex-direction: column;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm};
  background: linear-gradient(180deg, rgba(16, 17, 15, 0.00) 0%, rgba(16, 17, 15, 0.00) 6.67%, rgba(16, 17, 15, 0.01) 13.33%, rgba(16, 17, 15, 0.02) 20%, rgba(16, 17, 15, 0.04) 26.67%, rgba(16, 17, 15, 0.07) 33.33%, rgba(16, 17, 15, 0.10) 40%, rgba(16, 17, 15, 0.13) 46.67%, rgba(16, 17, 15, 0.17) 53.33%, rgba(16, 17, 15, 0.20) 60%, rgba(16, 17, 15, 0.23) 66.67%, rgba(16, 17, 15, 0.26) 73.33%, rgba(16, 17, 15, 0.28) 80%, rgba(16, 17, 15, 0.29) 86.67%, rgba(16, 17, 15, 0.30) 93.33%, rgba(16, 17, 15, 0.30) 100%);
  width: 100%;
  border-bottom-left-radius: ${({ theme }) => theme.radius.sm};
  border-bottom-right-radius: ${({ theme }) => theme.radius.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.xs};
  } 
`;

const LabelContentWrapperEmpty = styled(LabelContentWrapper)`
  background: none;
`;

const StyledWarning = styled(Typography)`
  padding-top: ${({ theme }) => theme.sizing.xs};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: ${({ theme }) => theme.sizing.xs};
  border-radius: ${({ theme }) => theme.radius.sm};
  background-color: ${({ theme }) => theme.fill.warningAlphaMain16};
  width: fit-content;
  margin-top: ${({ theme }) => theme.sizing.xs};
`;

const ErrorMessage = styled.div`
  display: flex;
  padding-top: ${({ theme }) => theme.sizing.xs};
`;